// src/Auth/ChatNotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import config from "../config";
import axios from "axios";
import useAuth from "./useAuth";

const ChatNotificationContext = createContext();

const useChatNotifications = () => {
  const context = useContext(ChatNotificationContext);
  if (!context) {
    throw new Error(
      "useChatNotifications must be used within a ChatNotificationProvider"
    );
  }
  return context;
};

const ChatNotificationProvider = ({ children }) => {
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [chats, setChats] = useState([]);
  const [sortedChats, setSortedChats] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({});
  
  // Use useAuth hook
  const auth = useAuth();
  const user = auth.user; // Get user from auth context
  const isAuthenticated = auth.isAuthenticated;

  axios.defaults.baseURL = config.BASE_URL;

  // Function to sort chats
  const sortChats = useCallback((chatsArray) => {
    return [...chatsArray].sort((a, b) => {
      const dateA = a.lastMessageTime || a.createdAt || new Date(0);
      const dateB = b.lastMessageTime || b.createdAt || new Date(0);
      return new Date(dateB) - new Date(dateA);
    });
  }, []);

  // Update sorted chats whenever chats change
  useEffect(() => {
    setSortedChats(sortChats(chats));
  }, [chats, sortChats]);

  const updateNotificationSetting = useCallback((chatId, enabled) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [chatId]: enabled,
    }));
  }, []);

  // Fetch chats and sync with auth provider
  const fetchChatsWithUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user || !user.username) return;

    try {
      const response = await axios.get(`/api/chats`, {
        withCredentials: true,
      });

      const chatsData = response.data;
      setChats(chatsData);

      // Sync unread count with AuthProvider
      if (auth.syncUnreadCountFromChats) {
        auth.syncUnreadCountFromChats(chatsData);
      }

      // Also calculate locally (optional)
      const totalUnread = chatsData.reduce(
        (total, chat) => total + (chat.unreadCount || 0),
        0
      );
      setUnreadChatCount(totalUnread);
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  }, [isAuthenticated, user, auth]);

  // Add a new chat to the top of the list
  const addNewChat = useCallback((newChat) => {
    setChats(prevChats => [newChat, ...prevChats]);
  }, []);

  // When a chat is marked as read
  const markChatAsRead = useCallback(async (chatId) => {
    try {
      await axios.post(
        `/api/chats/${chatId}/read`,
        {},
        { withCredentials: true }
      );

      // Update local state
      const updatedChats = chats.map((chat) =>
        chat._id === chatId ? { ...chat, unreadCount: 0 } : chat
      );
      setChats(updatedChats);

      // Sync with AuthProvider
      if (auth.syncUnreadCountFromChats) {
        auth.syncUnreadCountFromChats(updatedChats);
      }

      // Also update local count
      const totalUnread = updatedChats.reduce(
        (total, chat) => total + (chat.unreadCount || 0),
        0
      );
      setUnreadChatCount(totalUnread);
      
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  }, [chats, auth]);

  // Handle new message
  const handleNewMessage = useCallback((message) => {
    if (message.senderId === user?.username) return;

    const updatedChats = chats.map((chat) => {
      if (chat._id === message.chatId) {
        return {
          ...chat,
          lastMessage: message.content,
          lastMessageTime: message.timestamp,
          unreadCount: (chat.unreadCount || 0) + 1,
        };
      }
      return chat;
    });

    // Move the updated chat to top
    const chatIndex = updatedChats.findIndex(chat => chat._id === message.chatId);
    if (chatIndex > 0) {
      const [movedChat] = updatedChats.splice(chatIndex, 1);
      updatedChats.unshift(movedChat);
    }

    setChats(updatedChats);
    
    // Sync with AuthProvider
    if (auth.syncUnreadCountFromChats) {
      auth.syncUnreadCountFromChats(updatedChats);
    }

    // Update local count
    const totalUnread = updatedChats.reduce(
      (total, chat) => total + (chat.unreadCount || 0),
      0
    );
    setUnreadChatCount(totalUnread);
  }, [user, chats, auth]);

  // Toggle notifications for a chat
  const toggleChatNotifications = useCallback(async (chatId, enabled) => {
    try {
      await axios.post(
        `/api/chats/mute`,
        {
          chatId,
          notifications: enabled,
        },
        { withCredentials: true }
      );

      setChats((prev) =>
        prev.map((chat) => {
          if (chat._id === chatId) {
            return {
              ...chat,
              notifications: {
                ...chat.notifications,
                [user.username]: enabled,
              },
            };
          }
          return chat;
        })
      );
    } catch (error) {
      console.error("Error toggling chat notifications:", error);
    }
  }, [user]);

  // Refresh chats periodically
  useEffect(() => {
    if (isAuthenticated && user?.username) {
      fetchChatsWithUnreadCount();
      // const interval = setInterval(fetchChatsWithUnreadCount, 30000);
      // return () => clearInterval(interval);
    }
  }, [isAuthenticated, user, fetchChatsWithUnreadCount]);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      unreadChatCount,
      chats: sortedChats,
      fetchChatsWithUnreadCount,
      markChatAsRead,
      handleNewMessage,
      toggleChatNotifications,
      notificationSettings,
      updateNotificationSetting,
      addNewChat,
    }),
    [
      unreadChatCount,
      sortedChats,
      fetchChatsWithUnreadCount,
      markChatAsRead,
      handleNewMessage,
      toggleChatNotifications,
      notificationSettings,
      updateNotificationSetting,
      addNewChat,
    ]
  );

  return (
    <ChatNotificationContext.Provider value={contextValue}>
      {children}
    </ChatNotificationContext.Provider>
  );
};

export { useChatNotifications, ChatNotificationProvider };